import { CommonModule, Location } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  signal
} from '@angular/core';
import { RouterModule } from '@angular/router';

interface NavigationItem {
  id: string;
  text: string;
  href: string;
  group?: string;
}

interface DictionaryItem {
  campo: string;
  tipo: string;
  descricao: string;
}

@Component({
  selector: 'app-documentacao-api-page',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './documentacao-api.component.html',
  styleUrls: ['./documentacao-api.component.scss']
})
export class DocumentacaoApiPage implements OnInit {

  @ViewChild('searchInput')
  searchInput?: ElementRef<HTMLInputElement>;

  private sectionObserver?: IntersectionObserver;

  // =======================================================
  // STATE
  // =======================================================

  activeMenu = signal('');

  sidebarOpen = signal(false);

  theme = signal<'light' | 'dark'>('light');

  activeSection = signal('');

  toastVisible = signal(false);

  toastMessage = signal('Copiado.');

  navigationSearch = signal('');

  dictionarySearch = signal('');

  // =======================================================
  // MENU
  // =======================================================

  navigationItems = signal<NavigationItem[]>([
    {
      id: 'introducao',
      text: 'Introdução',
      href: '#introducao',
      group: 'Começando'
    },
    {
      id: 'pre-requisitos',
      text: 'Pré-requisitos',
      href: '#pre-requisitos',
      group: 'Começando'
    },
    {
      id: 'ambientes',
      text: 'Ambientes e URLs',
      href: '#ambientes',
      group: 'Começando'
    },
    {
      id: 'autenticacao',
      text: 'Autenticação',
      href: '#autenticacao',
      group: 'Começando'
    },
    {
      id: 'listar-todos',
      text: 'Listar Todos',
      href: '#listar-todos',
      group: 'Endpoints'
    },
    {
      id: 'listar-paginado',
      text: 'Listar Paginado',
      href: '#listar-paginado',
      group: 'Endpoints'
    },
    {
      id: 'filtrar-dn',
      text: 'Filtrar DN',
      href: '#filtrar-dn',
      group: 'Endpoints'
    }
  ]);

  filteredNavigation = computed(() => {

    const query =
      this.normalizeText(
        this.navigationSearch()
      );

    if (!query) {
      return this.navigationItems();
    }

    return this.navigationItems().filter(item =>
      this.normalizeText(item.text)
        .includes(query)
    );
  });

  // =======================================================
  // DICIONÁRIO
  // =======================================================

  dictionaryRows = signal<DictionaryItem[]>([
    {
      campo: 'idLead',
      tipo: 'Número',
      descricao: 'Identificador único do lead'
    },
    {
      campo: 'dtCadastro',
      tipo: 'Data',
      descricao: 'Data de cadastro'
    },
    {
      campo: 'nmNome',
      tipo: 'Texto',
      descricao: 'Nome do cliente'
    }
  ]);

  filteredDictionary = computed(() => {

    const query =
      this.normalizeText(
        this.dictionarySearch()
      );

    if (!query) {
      return this.dictionaryRows();
    }

    return this.dictionaryRows().filter(row => {

      const searchable = [
        row.campo,
        row.tipo,
        row.descricao
      ].join(' ');

      return this.normalizeText(searchable)
        .includes(query);
    });
  });

  dictionaryEmpty = computed(() =>
    this.filteredDictionary().length === 0
  );

  navigationEmpty = computed(() =>
    this.filteredNavigation().length === 0
  );



  dictionarySearch2 = signal('');

  updateDictionarySearch2(value: string): void {
    this.dictionarySearch2.set(value);
  }

  normalizeText2(value: string): string {
    return (value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }


  matchesDictionarySearch2(...values: string[]): boolean {

    const query = this.normalizeText(
      this.dictionarySearch2()
    );

    if (!query) {
      return true;
    }

    return this.normalizeText(
      values.join(' ')
    ).includes(query);
  }


  hasVisibleDictionaryRows(): boolean {
    const query = this.normalizeText2(
      this.dictionarySearch2()
    );

    const rows = [
      'idLead Número inteiro Identificador único do lead',
      'dtCadastro Data dd/MM/yyyy HH:mm:ss Data e hora de criação',
      'cdCampanha Texto até 20 caracteres Código da campanha',
      'nmCampanha Texto Nome da campanha',
      'nmTipoCampanha Texto Tipo da campanha',
      'idStatusLead Texto Status atual do lead',
      'cdDn Número inteiro Código da concessionária',
      'cdCpfCnpj Texto CPF ou CNPJ sem pontuação',
      'tpPessoa Texto Física ou Jurídica',
      'nmNome Texto Nome completo do cliente'
      // restante das linhas...
    ];

    return rows.some(row =>
      this.normalizeText(row)
        .includes(query)
    );
  }


  // =======================================================
  // TOC
  // =======================================================

  tocItems = signal<
    {
      id: string;
      title: string;
    }[]
  >([]);

  private toastTimeout?: number;

  // =======================================================
  // LIFE CYCLE
  // =======================================================


  constructor(private location: Location) { }

  ngOnInit(): void {
    this.loadTheme();
  }

  ngAfterViewInit(): void {
    this.buildTableOfContents();
    this.initializeIntersectionObserver();
  }

  ngOnDestroy(): void {

    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }

    this.sectionObserver?.disconnect();
  }

  // =======================================================
  // MENU
  // =======================================================

  toggleMenu(): void {

    const current = this.sidebarOpen();

    this.sidebarOpen.set(!current);

    document.body.style.overflow =
      !current ? 'hidden' : '';
  }

  closeMenu(): void {

    this.sidebarOpen.set(false);

    document.body.style.overflow = '';
  }

  // =======================================================
  // THEME
  // =======================================================

  loadTheme(): void {

    const stored =
      localStorage.getItem('docs-theme');

    if (
      stored === 'light' ||
      stored === 'dark'
    ) {
      this.applyTheme(stored);
      return;
    }

    const theme =
      window.matchMedia(
        '(prefers-color-scheme: dark)'
      ).matches
        ? 'dark'
        : 'light';

    this.applyTheme(theme);
  }

  toggleTheme(): void {

    this.applyTheme(
      this.theme() === 'dark'
        ? 'light'
        : 'dark'
    );
  }

  applyTheme(
    theme: 'light' | 'dark'
  ): void {

    this.theme.set(theme);

    document.documentElement.dataset['theme'] =
      theme;

    localStorage.setItem(
      'docs-theme',
      theme
    );
  }

  // =======================================================
  // SEARCH
  // =======================================================
  updateNavigationSearch(
    value: string
  ): void {
    this.navigationSearch.set(value);
  }

  updateDictionarySearch(
    value: string
  ): void {

    this.dictionarySearch.set(value);
  }

  normalizeText(
    value: string | null | undefined
  ): string {

    return (value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  // =======================================================
  // TOAST
  // =======================================================

  showToast(message: string): void {

    this.toastMessage.set(message);

    this.toastVisible.set(true);

    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }

    this.toastTimeout = window.setTimeout(() => {

      this.toastVisible.set(false);

    }, 1800);
  }

  matchesSearch(text: string): boolean {

    const query = this.normalizeText(
      this.navigationSearch()
    );

    if (!query) {
      return true;
    }

    return this.normalizeText(text)
      .includes(query);
  }

  hasVisibleLinks(): boolean {

    const query = this.normalizeText(
      this.navigationSearch()
    );

    const items = [
      'Introdução',
      'Pré-requisitos',
      'Ambientes e URLs',
      'Autenticação',
      'Regra de DN',
      'Primeiros passos',
      'Listar todos',
      'Listar com paginação',
      'Filtrar por DN',
      'DN com paginação',
      'DN e período',
      'Estrutura JSON',
      'Dicionário de dados',
      'Transformações',
      'Erros',
      'Deduplicação',
      'Frequência de consulta',
      'Perguntas frequentes',
      'Histórico de versões',
      'Contato e disponibilização de credenciais'
    ];

    return items.some(item =>
      this.normalizeText(item).includes(query)
    );
  }

  // =======================================================
  // COPY
  // =======================================================

  copyCode(event: Event): void {
    const button = event.currentTarget as HTMLElement;

    const code = button
      .closest('.code-panel')
      ?.querySelector('code');

    if (!code) {
      return;
    }


    console.log('copyCode :: ', code);


    navigator.clipboard.writeText(code.textContent?.trim() ?? '');
  }


  // =======================================================
  // SHORTCUTS
  // =======================================================

  @HostListener('document:keydown', ['$event'])
  onKeyDown(
    event: KeyboardEvent
  ): void {

    const target =
      event.target as HTMLElement;

    const isTyping =
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target?.isContentEditable;

    if (
      event.key === '/' &&
      !isTyping
    ) {

      event.preventDefault();

      this.searchInput
        ?.nativeElement
        .focus();
    }

    if (event.key === 'Escape') {

      this.closeMenu();

      this.searchInput
        ?.nativeElement
        .blur();
    }
  }

  // =======================================================
  // RESIZE
  // =======================================================

  @HostListener('window:resize')
  onResize(): void {

    if (
      window.innerWidth > 850 &&
      this.sidebarOpen()
    ) {
      this.closeMenu();
    }
  }

  // =======================================================
  // SECTION TRACKING
  // =======================================================

  private buildTableOfContents(): void {

    const sections = document.querySelectorAll(
      '.doc-section[id]'
    );

    const items = Array
      .from(sections)
      .map(section => {

        const title =
          section.querySelector(
            'h1,h2,h3'
          )?.textContent ?? '';

        return {
          id: section.id,
          title
        };
      });

    this.tocItems.set(items);
  }

  private initializeIntersectionObserver(): void {

    const sections = document.querySelectorAll(
      '.doc-section[id]'
    );

    this.sectionObserver =
      new IntersectionObserver(
        entries => {

          const visibleEntries =
            entries
              .filter(
                entry =>
                  entry.isIntersecting
              )
              .sort(
                (a, b) =>
                  a.boundingClientRect.top -
                  b.boundingClientRect.top
              );

          if (
            visibleEntries.length
          ) {

            this.activeSection.set(
              visibleEntries[0]
                .target.id
            );
          }
        },
        {
          rootMargin:
            '-72px 0px -68% 0px',
          threshold: [
            0,
            0.05,
            0.2
          ]
        }
      );

    sections.forEach(section =>
      this.sectionObserver?.observe(
        section
      )
    );
  }

  isActiveSection(
    id: string
  ): boolean {

    return (
      this.activeSection() === id
    );
  }

  trackById(
    _: number,
    item: { id: string }
  ): string {

    return item.id;
  }


  back(): void {
    this.location.back();
  }
}
